import { DatePipe } from '@angular/common';
import { Injectable, isDevMode, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';

import { Dialog } from '@app-core/services/dialog';
import { OverlayRef } from '@app-core/services/overlay';
import { SimpleDialogComponent } from '@app-shared/components/simple-dialog/simple-dialog.component';
import * as fromRoot from '@app-store/reducers';
import * as UserActions from '@app-store/user/user.actions';
import { JwtHelperService } from '@auth0/angular-jwt';
import { CookieService } from 'ngx-cookie-service';
import { fromEvent as observableFromEvent, Observable, Subscription, timer } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiService } from './api.service';
import { ConfigService } from './config.service';
import { LazyOktaService } from './okta/lazy-okta.service';
import {
  clearBoundOktaSession,
  decideIdpProbeEject,
  SessionConflictService,
} from './session-conflict.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private apiService = inject(ApiService);
  private jwtHelper = inject(JwtHelperService);
  private store = inject<Store<fromRoot.State>>(Store);
  private cookieService = inject(CookieService);
  private oktaService = inject(LazyOktaService);
  private _dialog = inject(Dialog);
  private _configService = inject(ConfigService);
  private _router = inject(Router);
  private _sessionConflict = inject(SessionConflictService);
  private _environment = this._configService.environment;

  token!: string;
  private _idpVisibilityListenerBound = false;
  private _idpProbeInFlight = false;

  private sessionTime: number = 5400000;
  private warningTime: number = 60000;
  private sessionTimer!: Observable<number>;
  private _sessionExpiringDialogOpened: boolean = false;

  // subscriptions
  private tokenTimerSubscription!: Subscription;
  private sessionTimerSubscription!: Subscription;
  private resetInactivitySubscription!: Subscription;
  private tokenApiCallSubscription!: Subscription;

  constructor() {
    this.initIdpSessionVisibilityListener();

    // Same-tab cross-portal: returning to this origin after End all elsewhere leaves stale
    // local tokens; visibility probe never fires on cold navigation — check once at boot.
    if (this._isOktaSso() && this._hasLocalDavinciSession()) {
      void this.probeIdpSessionOnVisible();
    }

  }

  startInactivityTimer(): void {
    this.resetInactivitySubscription?.unsubscribe();
    this.resetInactivitySubscription = observableFromEvent(document, 'click').subscribe(() => this.startSessionTimer());
  }

  startSessionTimer(): void {
    if (this._isOktaSso()) {
      void this.oktaService.captureBoundIdpSession();
    }
    this.cancelSessionTimer();
    this.sessionTimer = timer(this.sessionTime - this.warningTime, 1000).pipe(
      map((value) => this.warningTime - value * 1000)
    );

    this.sessionTimerSubscription = this.sessionTimer.subscribe((n) => {
      if (n === 0) {
        this.cancelAllSubscriptions();
        this.store.dispatch(new UserActions.LogOutAction({ davinciLogout: true, oktaLogout: false }));
      }
      if (!this._sessionExpiringDialogOpened && n !== 0) {
        this._openSessionExpiringDialog(n);
      }
    });
  }

  cancelSessionTimer(): void {
    this.sessionTimerSubscription?.unsubscribe();
  }

  cancelAllSubscriptions(): void {
    this.tokenTimerSubscription?.unsubscribe();
    this.sessionTimerSubscription?.unsubscribe();
    this.resetInactivitySubscription?.unsubscribe();
    this.tokenApiCallSubscription?.unsubscribe();
  }

  /** When this window is shown or focused, ask Okta if the IdP session still belongs to this tab. */
  initIdpSessionVisibilityListener(): void {
    if (this._idpVisibilityListenerBound || typeof document === 'undefined') {
      return;
    }

    this._idpVisibilityListenerBound = true;
    const onWindowReturned = (): void => {
      void this.probeIdpSessionOnVisible();
    };
    document.addEventListener('visibilitychange', onWindowReturned);
    window.addEventListener('focus', onWindowReturned);
  }

  async probeIdpSessionOnVisible(): Promise<boolean> {
    if (document.hidden || this._idpProbeInFlight) {
      return false;
    }

    const loggedIn = this._hasLocalDavinciSession();
    const onConflict = this._sessionConflict.isPortalConflict();
    const sso = this._isOktaSso();
    if (!sso || (!loggedIn && !onConflict)) {
      return false;
    }

    this._idpProbeInFlight = true;
    try {
      // ponytail: incognito can't see the Okta cookie, so "no session" is a guess; stay on the conflict page
      // (ejecting to login lets the widget sign in a second user over the foreign session).
      if (!loggedIn && (await this.oktaService.isIncognitoBrowser())) {
        return false;
      }
      const sessionStillOurs = loggedIn
        ? await this.oktaService.isBoundIdpSessionCurrent()
        : await this.oktaService.hasIdpSession();
      const idpAlive = sessionStillOurs ? true : await this.oktaService.hasIdpSession();
      const decision = decideIdpProbeEject({
        hidden: false,
        sso,
        loggedIn,
        onConflict,
        sessionStillOurs,
        idpAlive,
      });

      if (decision === 'conflict') {
        this._ejectToConflict();
        return true;
      }
      if (decision === 'login') {
        this._ejectToLogin();
        return true;
      }
      return false;
    } finally {
      this._idpProbeInFlight = false;
    }
  }

  isAuthenticated(): Promise<boolean> {
    if (
      this._configService.environment.authorization.flow === 'okta' &&
      (!isDevMode() || !localStorage.getItem('disable-okta') || localStorage.getItem('disable-okta') === 'false')
    ) {
      return Promise.all([this.oktaService.getOktaAuth(), this._davinciTokenValid()])
        .then(async ([oktaAuth, davinciTokenValid]) => {
          const oktaAuthenticated = await oktaAuth.isAuthenticated();
          return oktaAuthenticated && davinciTokenValid;
        })
        .catch(() => false);
    } else {
      return this._davinciTokenValid();
    }
  }

  scheduleTokenRenewal(): void {
    if (isDevMode() && !this._environment.tokenExpirationCheck) {
      return;
    }
    // cancel any token renewal subscriptions
    this.cancelTokenRenewal();

    // check authentication
    this.isAuthenticated().then((isAuthenticated) => {
      if (!isAuthenticated) {
        this.cancelAllSubscriptions();
        this.store.dispatch(new UserActions.LogOutAction({ davinciLogout: true, oktaLogout: false }));

        return;
      } else {
        const token = localStorage.getItem('token');

        if (!token) return;

        const tokenExpiration = this.jwtHelper.getTokenExpirationDate(token);
        const now = Date.now();
        const timeBeforeExpirationToStartRenewing = 2 * 60 * 1000;
        // when does it actually call?? is there a buffer before the call to the api?
        const expiresIn$ = timer(
          Math.max(1, tokenExpiration!.getTime() - timeBeforeExpirationToStartRenewing - now),
          1000
        );

        // Once the delay time from above is
        // reached, get a new JWT and schedule
        // additional refreshes
        this.tokenTimerSubscription = expiresIn$.subscribe(() => {
          if (!this._configService.environment.apiMock.enabled) {
            this.renewToken();
          }
        });
      }
    });
  }

  cancelTokenRenewal(): void {
    this.tokenTimerSubscription?.unsubscribe();
  }

  private _davinciTokenValid(): Promise<boolean> {
    return new Promise((resolve) => {
      const user = JSON.parse(localStorage.getItem('userState')!);
      if (isDevMode() && !this._environment.tokenExpirationCheck) {
        resolve(user.isLoggedIn);

        return;
      }
      // Check whether the current time is past the
      // access token's expiry time
      const token = localStorage.getItem('token');
      if (token && user.isLoggedIn) {
        try {
          const tokenExpiration = this.jwtHelper.getTokenExpirationDate(token);
          resolve(
            this._configService.environment.apiMock.enabled ||
            (tokenExpiration !== null && Date.now() < tokenExpiration!.getTime())
          );
        } catch (error) {
          localStorage.removeItem('token');
          resolve(false);
        }
      } else {
        resolve(false);
      }
    });
  }

  private _isOktaSso(): boolean {
    return this._configService.environment.authorization.flow === 'okta';
  }

  private _hasLocalDavinciSession(): boolean {
    try {
      const user = JSON.parse(localStorage.getItem('userState') ?? 'null');
      return !!user?.isLoggedIn && !!localStorage.getItem('token');
    } catch {
      return false;
    }
  }

  private _ejectToLogin(): void {
    this.cancelAllSubscriptions();
    clearBoundOktaSession();
    this._sessionConflict.clearDeferredConflict();
    this.store.dispatch(new UserActions.LogOutAction({ davinciLogout: true, oktaLogout: false }));
  }

  private _ejectToConflict(): void {
    this.cancelAllSubscriptions();
    clearBoundOktaSession();
    localStorage.removeItem('token');
    this.store.dispatch(new UserActions.LogOutSuccessAction());
    this._sessionConflict.deferConflict();
    void this._router.navigate(['/callback/error']);
  }

  private renewToken(): void {
    this.tokenApiCallSubscription?.unsubscribe();

    this.tokenApiCallSubscription = this.apiService.refreshToken().subscribe((data: { token: string }) => {
      // If successful, dispatch success action with result
      localStorage.setItem('token', data.token);
      this.scheduleTokenRenewal();
    });
  }

  getCookie(): string {
    return this.cookieService.get('sso-token');
  }

  ngOnDestroy(): void {
    this.tokenTimerSubscription?.unsubscribe();
    this.sessionTimerSubscription?.unsubscribe();
    this.resetInactivitySubscription?.unsubscribe();
    this.tokenApiCallSubscription?.unsubscribe();
  }

  private _openSessionExpiringDialog(val: number): void {
    const timeoutExpiring = new DatePipe('en-US').transform(val, 'mm:ss');
    const innerHtml = `<p>Your session is about to expire. Click OK to continue working.<br>Expiring in: ${timeoutExpiring}</p>`;
    this._sessionExpiringDialogOpened = true;

    this._dialog
      .open(SimpleDialogComponent, {
        data: {
          actions: [
            { text: 'Ok', act: (ref: OverlayRef<SimpleDialogComponent>) => ref.close(), buttonType: 'success' },
          ],
          value: { title: 'Session Expiring!', innerHtml },
        },
      })
      .afterClosed()
      .subscribe(() => (this._sessionExpiringDialogOpened = false));
  }
}
